import React, { useEffect, useState } from 'react';
import { ArrowRight, BookOpen, Sparkles } from 'lucide-react';
import FoundingPioneerBadge from './FoundingPioneerBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * HomeInlineCommerce — above-the-fold enrollment section on the homepage.
 *
 * Fetches the top 3 courses from the backend and renders inline
 * "Enroll Now" tiles that route straight into the course-detail /
 * checkout flow.  NextXus Prompts Skills pricing is surfaced on every tile.
 */
export default function HomeInlineCommerce() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/courses/`);
        const data = await r.json();
        if (!cancelled) {
          setCourses(Array.isArray(data) ? data.slice(0, 3) : []);
        }
      } catch {
        if (!cancelled) setCourses([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section
      data-testid="home-inline-commerce"
      aria-label="Enroll instantly — NextXus Prompts Skills classes"
      className="relative bg-gradient-to-b from-black via-purple-950/20 to-black border-y border-amber-400/20"
    >
      <div className="container mx-auto px-4 py-14">
        <header className="text-center max-w-2xl mx-auto mb-8">
          <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.32em] text-amber-300/85 mb-2">
            <Sparkles className="w-3.5 h-3.5" />
            NextXus Prompts Skills — Enrollment
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">
            Start Learning <span className="text-amber-300">Right Now</span>
          </h2>
          <p className="mt-3 text-sm md:text-base text-gray-300">
            Three flagship courses. Inline enrollment. No detours.
          </p>
        </header>

        {loading ? (
          <div className="text-center py-12" data-testid="home-commerce-loading">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-400 mx-auto"></div>
            <p className="mt-3 text-xs text-gray-400 uppercase tracking-[0.18em] font-mono">
              Loading top courses…
            </p>
          </div>
        ) : courses.length === 0 ? (
          <p
            className="text-center text-sm text-gray-400"
            data-testid="home-commerce-empty"
          >
            The catalog is being inscribed — check back shortly.
          </p>
        ) : (
          <div
            className="grid md:grid-cols-3 gap-6"
            data-testid="home-commerce-grid"
          >
            {courses.map((c) => {
              const listUsd =
                typeof c.price_usd === 'number'
                  ? c.price_usd
                  : typeof c.price === 'number'
                  ? c.price
                  : 0;
              const originalUsd = listUsd > 0 ? Math.round(listUsd * 2) : 0;
              return (
                <article
                  key={c.course_id || c.id}
                  data-testid={`home-commerce-card-${c.course_id || c.id}`}
                  className="group rounded-2xl border-2 border-purple-500/30 bg-gradient-to-b from-gray-900 via-black to-black hover:border-amber-400/60 transition-colors flex flex-col shadow-lg shadow-purple-500/10 overflow-hidden"
                >
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-purple-500/20 border border-purple-400/40">
                        <BookOpen className="w-4 h-4 text-purple-200" />
                      </span>
                      <FoundingPioneerBadge
                        compact
                        originalUsd={originalUsd}
                        pioneerUsd={listUsd}
                      />
                    </div>
                    <h3 className="text-lg font-bold text-white leading-tight mb-2">
                      {c.title}
                    </h3>
                    <p className="text-xs text-gray-400 leading-relaxed line-clamp-3 flex-1">
                      {c.description || c.summary || 'Federation-aligned curriculum.'}
                    </p>
                    <div className="mt-4 flex items-baseline justify-between">
                      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-300/85">
                        Pioneer Rate
                      </span>
                      <span className="text-xl font-extrabold text-white">
                        {listUsd === 0 ? 'Free' : `$${listUsd}`}
                      </span>
                    </div>
                  </div>
                  <a
                    href={`/course/${c.course_id || c.id}`}
                    data-testid={`home-commerce-enroll-${c.course_id || c.id}`}
                    className="mt-auto flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-black font-bold py-3 text-sm transition-colors"
                  >
                    Enroll Now <ArrowRight className="w-4 h-4" />
                  </a>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
